import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import NewReferralPage from "./pages/NewReferralPage";
import ReferralDetailPage from "./pages/ReferralDetailPage";
import GuidelinesPage from "./pages/GuidelinesPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import ConsultantDashboardPage from "./pages/ConsultantDashboardPage";
import ConsultantCalendarPage from "./pages/ConsultantCalendarPage";
import InvestorPitchPage from "./pages/InvestorPitchPage";
import TutorialPage from "./pages/TutorialPage";
import MarketingMaterialsPage from "./pages/MarketingMaterialsPage";
import ResourcesPage from "./pages/ResourcesPage";

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
    const { isAuthenticated, loading, user } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }
    
    // Check role-based access
    if (roles && !roles.includes(user?.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/new-referral" 
                element={
                    <ProtectedRoute roles={['doctor', 'primary_care']}>
                        <NewReferralPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/referral/:id" 
                element={
                    <ProtectedRoute>
                        <ReferralDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/guidelines" 
                element={
                    <ProtectedRoute>
                        <GuidelinesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/analytics" 
                element={
                    <ProtectedRoute roles={['institution_admin', 'nhs_manager']}>
                        <AnalyticsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/consultant" 
                element={
                    <ProtectedRoute roles={['consultant', 'institution_admin']}>
                        <ConsultantDashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/consultant/calendar" 
                element={
                    <ProtectedRoute roles={['consultant', 'institution_admin']}>
                        <ConsultantCalendarPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute roles={['super_admin']}>
                        <AdminPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <ProfilePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/investor-pitch" 
                element={<InvestorPitchPage />} 
            />
            <Route 
                path="/tutorial" 
                element={<TutorialPage />} 
            />
            <Route 
                path="/marketing" 
                element={<MarketingMaterialsPage />} 
            />
            <Route 
                path="/resources" 
                element={<ResourcesPage />} 
            />
            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// Mobile Navigation Wrapper
function MobileNavWrapper() {
    const { isAuthenticated } = useAuth();
    const MobileNav = React.lazy(() => import('./components/MobileNav'));
    
    if (!isAuthenticated) return null;
    
    return (
        <React.Suspense fallback={null}>
            <MobileNav />
        </React.Suspense>
    );
}

// Guidelines Chatbot Wrapper
function ChatbotWrapper() {
    const { isAuthenticated } = useAuth();
    const GuidelinesChatbot = React.lazy(() => import('./components/GuidelinesChatbot'));
    
    if (!isAuthenticated) return null;
    
    return (
        <React.Suspense fallback={null}>
            <GuidelinesChatbot />
        </React.Suspense>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <div className="App min-h-screen bg-background text-foreground pb-16 md:pb-0">
                        <AppRoutes />
                        <MobileNavWrapper />
                        <ChatbotWrapper />
                        <React.Suspense fallback={null}>
                            {React.createElement(React.lazy(() => import('./components/common/PWAInstallPrompt')))}
                        </React.Suspense>
                        <Toaster position="top-right" richColors closeButton />
                    </div>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
